import type { ComparisonContext } from '@shared/providers/comparison-context/ComparisonContext.ts';
import type { PandaProductComparisonApi } from '@shared/types/PandaProductComparisonApi.ts';
import type { Variation, VariationV2 } from '@shared/types/types.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';

export class PandaComparison {
    private pandaApi: PandaProductComparisonApi | null = null;

    constructor(
        private variationLoadingService: VariationLoader,
        private trackingService: TrackingPort,
        private eventsIncoming: EventsIncoming,
    ) {}

    initialize(context: ComparisonContext) {
        this.eventsIncoming.onComparisonApiReady(async (api: PandaProductComparisonApi) => {
            this.pandaApi = api;
            await this.updateContext(context);
            context.initialized = true;
        });
    }

    async updateContext(context: ComparisonContext) {
        if (!this.pandaApi) return;

        const contextId = context.comparisonId;
        if (contextId) {
            const variationIds = (await this.pandaApi.getList(contextId)) || [];
            context.selectedVariations = await this.variationLoadingService.loadVariations(variationIds);
            context.compareLink = await this.pandaApi.getLinkForList(contextId);
        } else {
            context.selectedVariations = [];
            context.compareLink = null;
        }
    }

    async selectVariation(context: ComparisonContext, variation: Variation | VariationV2) {
        const contextId = context.comparisonId;
        if (!this.pandaApi || !contextId) return;

        context.selectedVariations.push(variation);
        await this.pandaApi.addToList(contextId, variation.variationId);
        this.trackingService.trackComparisonActivity('selected');
        context.compareLink = await this.pandaApi.getLinkForList(contextId);
    }

    async deselectVariation(context: ComparisonContext, id: string) {
        const contextId = context.comparisonId;
        if (!this.pandaApi || !contextId) return;

        const idx = context.selectedVariations.findIndex((variation) => variation.variationId === id);
        if (idx !== -1) {
            context.selectedVariations.splice(idx, 1);
        }
        await this.pandaApi.removeFromList(contextId, id);
        this.trackingService.trackComparisonActivity('deselected');
        context.compareLink = await this.pandaApi.getLinkForList(contextId);
    }

    async clearSelectedVariations(context: ComparisonContext) {
        const contextId = context.comparisonId;
        if (!this.pandaApi || !contextId) return;

        context.selectedVariations = [];
        await this.pandaApi.removeList(contextId);
        context.compareLink = null;
    }
}
